/**
 * /cost-codes — per-entity cost-code taxonomy admin (DataGridPro).
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ Header                                                   │
 *   │ AI assist banner: [Interview] [Upload] [Apply Template]  │
 *   │ Toolbar: + New code, status filter                       │
 *   │ ┌──────────────────────────────────────────────────────┐ │
 *   │ │ DataGridPro tree-data                                │ │
 *   │ │   path = parent chain by code (Building / Activity / │ │
 *   │ │          Modifier / Extension)                       │ │
 *   │ │   columns: code · name · status pill · cost type ·   │ │
 *   │ │     uom · sort · provenance · row actions            │ │
 *   │ └──────────────────────────────────────────────────────┘ │
 *   └──────────────────────────────────────────────────────────┘
 *
 * Right drawer (when row selected):
 *   - Mappings: list + add form for ext-system mappings (Viewpoint, etc.)
 *   - Lifecycle: status transition picker w/ reason
 *   - Clone for PCO: opens clone dialog inline
 *
 * Adding a new template: see /cost-code-templates page.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGridPro,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowParams,
} from "@mui/x-data-grid-pro";
import { gridDefaults, FlexGrid } from "@/grids/gridDefaults";

import { api } from "@/lib/api";
import {
  costCodeKeys,
  costCodeMappingsQueryOptions,
  costCodeTemplatesQueryOptions,
  costCodeTemplateKeys,
  costCodesListQueryOptions,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import type {
  CostCode,
  CostCodeCreationSourceStr,
  CostCodeMapping,
  CostCodeStatusStr,
  CostCodeTemplate,
  CostCodeTemplateApplyResult,
  CostTypeStr,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/cost-codes")({
  component: CostCodesPage,
});

const COST_TYPES: CostTypeStr[] = [
  "labor", "material", "subcontract", "equipment", "other", "burden",
];

const COST_TYPE_COLOR: Record<CostTypeStr, string> = {
  labor:       "#2563eb",
  material:    "#0891b2",
  subcontract: "#9333ea",
  equipment:   "#65a30d",
  other:       "#64748b",
  burden:      "#dc2626",
};

const STATUS_COLOR: Record<CostCodeStatusStr, { bg: string; fg: string }> = {
  draft:            { bg: "#f1f5f9", fg: "#475569" },
  active:           { bg: "#dcfce7", fg: "#166534" },
  pricing:          { bg: "#dbeafe", fg: "#1e40af" },
  pending_approval: { bg: "#fef3c7", fg: "#92400e" },
  approved:         { bg: "#d1fae5", fg: "#065f46" },
  rejected:         { bg: "#fee2e2", fg: "#991b1b" },
  billed:           { bg: "#e0e7ff", fg: "#3730a3" },
  closed:           { bg: "#e5e7eb", fg: "#374151" },
  inactive:         { bg: "#f3f4f6", fg: "#6b7280" },
};

const ALL_STATUSES: CostCodeStatusStr[] = [
  "draft", "active", "pricing", "pending_approval", "approved",
  "rejected", "billed", "closed", "inactive",
];

const SOURCE_LABEL: Record<CostCodeCreationSourceStr, string> = {
  manual:                  "Manual",
  imported:                "Imported",
  cloned:                  "Cloned",
  template_applied:        "Template",
  ai_drafted:              "AI draft",
  ai_suggested_confirmed:  "AI ✓",
};


function CostCodesPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [showInactive, setShowInactive] = useState(false);
  const [selectedCode, setSelectedCode] = useState<CostCode | null>(null);
  const [creating, setCreating] = useState(false);
  const [cloning, setCloning] = useState<CostCode | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);

  const codesQuery = useQuery(costCodesListQueryOptions(showInactive));
  const codes = codesQuery.data?.items ?? [];

  // Build tree-data path for DataGridPro: walk parent_id chain to root.
  const rowsWithPath = useMemo(() => {
    const byId = new Map(codes.map((c) => [c.id, c]));
    const pathFor = (c: CostCode): string[] => {
      const chain: string[] = [];
      let cur: CostCode | undefined = c;
      const safety = 8;
      let i = 0;
      while (cur && i < safety) {
        chain.unshift(cur.code);
        cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
        i++;
      }
      return chain;
    };
    return codes.map((c) => ({ ...c, path: pathFor(c) }));
  }, [codes]);

  const deactivateMutation = useMutation({
    mutationFn: (id: string) =>
      api(`/api/cost-codes/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: costCodeKeys.all });
      toast.success("Cost code deactivated");
    },
    onError: (err: Error) => toast.error(`Deactivate failed: ${err.message}`),
  });

  const startInterview = () => {
    const prompt = encodeURIComponent(
      "Help me set up cost codes for this entity. Ask me 4-6 questions " +
      "about the trades I self-perform vs subcontract, project types, " +
      "and any naming conventions I use. Then propose a tailored " +
      "cost-code list and call propose_cost_codes. After I confirm, " +
      "call import_cost_codes to apply.",
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigate({ to: "/chat" as any, search: { prompt } });
  };

  // ──────────────────────────────────────────────────────────────
  // Grid columns
  // ──────────────────────────────────────────────────────────────
  const columns = useMemo<GridColDef<typeof rowsWithPath[number]>[]>(
    () => [
      {
        field: "code",
        headerName: "Code",
        width: 160,
        renderCell: (p) => (
          <span style={{ fontFamily: "monospace", fontSize: 12 }}>
            {p.row.code}
          </span>
        ),
      },
      { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
      {
        field: "level",
        headerName: "L",
        width: 50,
        type: "number",
        renderCell: (p) => (
          <Chip
            size="small"
            label={`L${p.row.level}`}
            sx={{
              fontSize: 10, height: 18,
              bgcolor: "#eef2ff", color: "#3730a3",
            }}
          />
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        renderCell: (p) => {
          const s = STATUS_COLOR[p.row.status];
          return (
            <Chip
              size="small"
              label={p.row.status.replace(/_/g, " ")}
              sx={{
                fontSize: 10, height: 20,
                bgcolor: s.bg, color: s.fg,
                fontWeight: 600,
              }}
            />
          );
        },
      },
      {
        field: "default_cost_type",
        headerName: "Cost type",
        width: 110,
        renderCell: (p) => {
          const ct = p.row.default_cost_type;
          if (!ct) return <span style={{ color: "#94a3b8" }}>—</span>;
          return (
            <Chip
              size="small"
              label={ct}
              sx={{
                fontSize: 10, height: 18,
                bgcolor: `${COST_TYPE_COLOR[ct]}1a`,
                color: COST_TYPE_COLOR[ct],
                fontWeight: 600,
              }}
            />
          );
        },
      },
      { field: "unit_of_measure", headerName: "UoM", width: 70 },
      {
        field: "creation_source",
        headerName: "Source",
        width: 95,
        renderCell: (p) => (
          <span style={{ fontSize: 11, color: "#64748b" }}>
            {SOURCE_LABEL[p.row.creation_source]}
          </span>
        ),
      },
      { field: "sort_order", headerName: "Sort", width: 65, type: "number" },
      {
        field: "actions",
        headerName: "",
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams) => {
          const row = p.row as CostCode;
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <IconButton
                size="small"
                onClick={() => setSelectedCode(row)}
                title="Edit / mappings / lifecycle"
              >
                ✎
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setCloning(row)}
                title="Clone for PCO / CO"
              >
                ⎘
              </IconButton>
              {row.status === "active" && (
                <IconButton
                  size="small"
                  onClick={() => deactivateMutation.mutate(row.id)}
                  title="Deactivate"
                >
                  🗑
                </IconButton>
              )}
            </div>
          );
        },
      },
    ],
    [deactivateMutation],
  );

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Cost Codes</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Per-entity hierarchical taxonomy with lifecycle, templates,
            and external mappings
          </span>
          <div className="flex-1" />
          <Button
            size="small"
            variant="text"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onClick={() => navigate({ to: "/cost-code-templates" as any })}
          >
            Templates →
          </Button>
        </div>
      </div>

      {/* AI assist banner */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-indigo-100 px-6 py-3 dark:from-indigo-950/40 dark:to-blue-950/40 dark:border-indigo-900/50">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-lg">✨</span>
            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
              Set up cost codes the fast way:
            </span>
          </div>
          <div className="flex-1" />
          <Button
            size="small" variant="outlined" color="primary"
            onClick={startInterview}
          >
            🎙️ Interview me
          </Button>
          <Button
            size="small" variant="outlined" color="primary"
            onClick={() => setUploadOpen(true)}
          >
            📤 Upload existing list
          </Button>
          <Button
            size="small" variant="outlined" color="primary"
            onClick={() => setApplyTemplateOpen(true)}
          >
            📋 Apply template
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 px-6 py-2 flex items-center gap-3 dark:bg-gray-900 dark:border-gray-800">
        <Button
          size="small" variant="contained" onClick={() => setCreating(true)}
        >
          + New code
        </Button>
        <div className="flex-1" />
        <label className="text-xs text-slate-600 flex items-center gap-1 dark:text-slate-300">
          <Switch
            size="small"
            checked={showInactive}
            onChange={(_, v) => setShowInactive(v)}
          />
          Show inactive
        </label>
        <span className="text-xs text-slate-500 ml-2 dark:text-slate-400">{codes.length} codes</span>
      </div>

      {/* Grid */}
      <div className="flex-1 min-h-0 px-6 py-3">
        <FlexGrid>
          <DataGridPro
            {...gridDefaults}
            rows={rowsWithPath}
            columns={columns}
            treeData
            getTreeDataPath={(row) => row.path}
            groupingColDef={{
              headerName: "Hierarchy",
              width: 220,
              hideDescendantCount: false,
            }}
            defaultGroupingExpansionDepth={2}
            getRowId={(row) => row.id}
            loading={codesQuery.isLoading}
            density="compact"
            initialState={{
              sorting: { sortModel: [{ field: "sort_order", sort: "asc" }] },
            }}
            onRowClick={(p: GridRowParams) => setSelectedCode(p.row as CostCode)}
            sx={{
              "& .MuiDataGrid-row": { cursor: "pointer" },
            }}
          />
        </FlexGrid>
      </div>

      {/* Edit / mappings / lifecycle drawer */}
      <Drawer
        anchor="right"
        open={selectedCode !== null}
        onClose={() => setSelectedCode(null)}
        PaperProps={{ sx: { width: 480 } }}
      >
        {selectedCode && (
          <CostCodeDrawer
            costCode={selectedCode}
            onClose={() => setSelectedCode(null)}
          />
        )}
      </Drawer>

      {creating && <CostCodeFormDialog target={null} onClose={() => setCreating(false)} />}
      {cloning && (
        <CloneDialog
          source={cloning}
          onClose={() => setCloning(null)}
        />
      )}
      {uploadOpen && <CostCodeUploadDialog onClose={() => setUploadOpen(false)} />}
      {applyTemplateOpen && (
        <ApplyTemplateDialog onClose={() => setApplyTemplateOpen(false)} />
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Drawer — edit + lifecycle + mappings for a selected cost code
// ──────────────────────────────────────────────────────────────
function CostCodeDrawer({
  costCode, onClose,
}: {
  costCode: CostCode;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<"edit" | "lifecycle" | "mappings">("edit");

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-semibold">{costCode.code}</span>
          <Chip
            size="small"
            label={`L${costCode.level}`}
            sx={{
              fontSize: 10, height: 18,
              bgcolor: "#eef2ff", color: "#3730a3",
            }}
          />
          <Chip
            size="small"
            label={costCode.status.replace(/_/g, " ")}
            sx={{
              fontSize: 10, height: 20,
              bgcolor: STATUS_COLOR[costCode.status].bg,
              color: STATUS_COLOR[costCode.status].fg,
              fontWeight: 600,
            }}
          />
          <div className="flex-1" />
          <IconButton size="small" onClick={onClose}>✕</IconButton>
        </div>
        <div className="text-sm text-slate-700 mt-1 dark:text-slate-300">{costCode.name}</div>
      </div>

      <div className="border-b border-slate-200 flex dark:border-gray-800">
        {(["edit", "lifecycle", "mappings"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={
              "flex-1 px-3 py-2 text-xs uppercase tracking-wide " +
              (tab === t
                ? "bg-blue-50 text-blue-700 border-b-2 border-blue-500"
                : "text-slate-600 hover:bg-slate-50")
            }
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-4">
        {tab === "edit" && (
          <CostCodeEditPanel costCode={costCode} onSaved={onClose} />
        )}
        {tab === "lifecycle" && (
          <LifecyclePanel costCode={costCode} onSaved={(cc) => {
            qc.invalidateQueries({ queryKey: costCodeKeys.all });
            toast.success(`Status → ${cc.status}`);
          }} />
        )}
        {tab === "mappings" && <MappingsPanel costCode={costCode} />}
      </div>
    </div>
  );
}


function CostCodeEditPanel({
  costCode, onSaved,
}: {
  costCode: CostCode;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(costCode.name);
  const [division, setDivision] = useState(costCode.division ?? "");
  const [uom, setUom] = useState(costCode.unit_of_measure ?? "");
  const [costType, setCostType] = useState<CostTypeStr | "">(
    costCode.default_cost_type ?? "",
  );
  const [sort, setSort] = useState(String(costCode.sort_order));
  const [notes, setNotes] = useState(costCode.notes ?? "");

  const saveMut = useMutation({
    mutationFn: () =>
      api(`/api/cost-codes/${costCode.id}`, {
        method: "PATCH",
        body: {
          name, division: division || null,
          unit_of_measure: uom || null,
          default_cost_type: (costType || null) as CostTypeStr | null,
          sort_order: Number(sort) || 0,
          notes: notes || null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: costCodeKeys.all });
      toast.success("Cost code updated");
      onSaved();
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  return (
    <div className="space-y-3">
      <TextField label="Name" size="small" fullWidth
        value={name} onChange={(e) => setName(e.target.value)} />
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Division" size="small"
          value={division} onChange={(e) => setDivision(e.target.value)} />
        <TextField label="UoM" size="small"
          value={uom} onChange={(e) => setUom(e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <TextField select size="small" label="Default cost type"
          value={costType}
          onChange={(e) => setCostType(e.target.value as CostTypeStr | "")}>
          <MenuItem value="">— none —</MenuItem>
          {COST_TYPES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField label="Sort" type="number" size="small"
          value={sort} onChange={(e) => setSort(e.target.value)} />
      </div>
      <TextField label="Notes" size="small" multiline minRows={2} fullWidth
        value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div className="text-[11px] text-slate-500 dark:text-slate-400">
        Source: <span className="font-mono">{SOURCE_LABEL[costCode.creation_source]}</span> ·
        Updated: <span className="font-mono">{new Date(costCode.updated_at).toLocaleString()}</span>
      </div>
      <Button variant="contained" onClick={() => saveMut.mutate()}
        disabled={saveMut.isPending}>
        {saveMut.isPending ? "Saving…" : "Save"}
      </Button>
    </div>
  );
}


function LifecyclePanel({
  costCode, onSaved,
}: {
  costCode: CostCode;
  onSaved: (cc: CostCode) => void;
}) {
  const [newStatus, setNewStatus] = useState<CostCodeStatusStr>(costCode.status);
  const [reason, setReason] = useState("");

  const transition = useMutation({
    mutationFn: () =>
      api<CostCode>(`/api/cost-codes/${costCode.id}/status`, {
        method: "POST",
        body: { status: newStatus, reason: reason || null },
      }),
    onSuccess: (cc) => onSaved(cc),
  });

  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-600 dark:text-slate-300">
        Move this cost code through its lifecycle. Common transitions:
        draft → active → pricing → pending_approval → approved → billed → closed.
      </div>
      <TextField select size="small" fullWidth label="New status"
        value={newStatus}
        onChange={(e) => setNewStatus(e.target.value as CostCodeStatusStr)}>
        {ALL_STATUSES.map((s) => (
          <MenuItem key={s} value={s}>{s.replace(/_/g, " ")}</MenuItem>
        ))}
      </TextField>
      <TextField label="Reason (logged for audit)" size="small" fullWidth
        multiline minRows={2}
        value={reason} onChange={(e) => setReason(e.target.value)} />
      <Button variant="contained"
        disabled={newStatus === costCode.status || transition.isPending}
        onClick={() => transition.mutate()}>
        {transition.isPending ? "Updating…" : "Apply"}
      </Button>
    </div>
  );
}


function MappingsPanel({ costCode }: { costCode: CostCode }) {
  const qc = useQueryClient();
  const toast = useToast();
  const mapsQuery = useQuery(costCodeMappingsQueryOptions(costCode.id));
  const maps = mapsQuery.data ?? [];

  const [system, setSystem] = useState("");
  const [code, setCode] = useState("");
  const [desc, setDesc] = useState("");

  const upsertMut = useMutation({
    mutationFn: () =>
      api<CostCodeMapping>(`/api/cost-codes/${costCode.id}/mappings`, {
        method: "POST",
        body: {
          target_system: system,
          target_code: code,
          target_description: desc || null,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: costCodeKeys.all });
      toast.success(`Mapped → ${system}: ${code}`);
      setSystem(""); setCode(""); setDesc("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deactivateMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/cost-codes/mappings/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: costCodeKeys.all });
      toast.success("Mapping deactivated");
    },
  });

  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-600 dark:text-slate-300">
        Map this Bynaus cost code to external systems
        (Viewpoint, CMiC, Sage, payroll buckets, customer formats).
        Bynaus stays the source of truth.
      </div>

      {/* Existing mappings */}
      <div className="border border-slate-200 rounded dark:border-gray-800">
        {mapsQuery.isLoading ? (
          <div className="p-3 text-xs text-slate-500 dark:text-slate-400">Loading…</div>
        ) : maps.length === 0 ? (
          <div className="p-3 text-xs text-slate-500 italic dark:text-slate-400">No mappings.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-600 dark:bg-gray-950 dark:text-slate-300">
              <tr>
                <th className="text-left px-2 py-1">System</th>
                <th className="text-left px-2 py-1">External code</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {maps.map((m) => (
                <tr key={m.id} className="border-t border-slate-100 dark:border-gray-800">
                  <td className="px-2 py-1 font-mono">{m.target_system}</td>
                  <td className="px-2 py-1">
                    <span className="font-mono">{m.target_code}</span>
                    {m.target_description && (
                      <div className="text-[10px] text-slate-500 dark:text-slate-400">{m.target_description}</div>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <IconButton size="small"
                      onClick={() => deactivateMut.mutate(m.id)}>
                      ✕
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add new mapping */}
      <div className="border border-slate-200 rounded p-3 bg-slate-50 space-y-2 dark:bg-gray-950 dark:border-gray-800">
        <div className="text-xs font-medium text-slate-700 dark:text-slate-300">Add mapping</div>
        <TextField label="Target system" size="small" fullWidth
          placeholder="Viewpoint / CMiC / Sage / Payroll-X"
          value={system} onChange={(e) => setSystem(e.target.value)} />
        <TextField label="External code" size="small" fullWidth
          value={code} onChange={(e) => setCode(e.target.value)} />
        <TextField label="External description (optional)" size="small" fullWidth
          value={desc} onChange={(e) => setDesc(e.target.value)} />
        <Button size="small" variant="contained"
          disabled={!system || !code || upsertMut.isPending}
          onClick={() => upsertMut.mutate()}>
          {upsertMut.isPending ? "Saving…" : "Add mapping"}
        </Button>
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Clone dialog (PCO / CO fast-create)
// ──────────────────────────────────────────────────────────────
function CloneDialog({
  source, onClose,
}: {
  source: CostCode;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [modifier, setModifier] = useState<string>("CO");
  const [extension, setExtension] = useState<string>("");
  const [name, setName] = useState<string>(`${source.name} - Change Order`);

  const newCode = extension
    ? `${source.code}-${modifier}-${extension}`
    : `${source.code}-${modifier}`;

  const cloneMut = useMutation({
    mutationFn: () =>
      api<CostCode>(`/api/cost-codes/${source.id}/clone`, {
        method: "POST",
        body: {
          new_code: newCode,
          new_name: name,
          modifier_label: modifier,
        },
      }),
    onSuccess: (cc) => {
      qc.invalidateQueries({ queryKey: costCodeKeys.all });
      toast.success(`Created ${cc.code} (DRAFT)`);
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Clone <span className="font-mono">{source.code}</span> for PCO / CO
      </DialogTitle>
      <DialogContent dividers>
        <div className="space-y-3 pt-2">
          <div className="text-xs text-slate-600 dark:text-slate-300">
            Creates a level-{source.level + 1} child with the same default
            cost type and UoM. Starts in DRAFT — promote to ACTIVE when
            the change is approved.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <TextField select label="Modifier" size="small" value={modifier}
              onChange={(e) => {
                setModifier(e.target.value);
                const m = e.target.value;
                const human = m === "CO" ? "Change Order"
                  : m === "PCO" ? "PCO"
                  : m === "TM" ? "T&M"
                  : m === "REWORK" ? "Rework"
                  : m === "BACKCHRG" ? "Backcharge"
                  : m;
                setName(`${source.name} - ${human}`);
              }}>
              <MenuItem value="CO">CO (Change Order)</MenuItem>
              <MenuItem value="PCO">PCO</MenuItem>
              <MenuItem value="TM">T&M</MenuItem>
              <MenuItem value="REWORK">Rework</MenuItem>
              <MenuItem value="BACKCHRG">Backcharge</MenuItem>
            </TextField>
            <TextField label="Extension (optional)" size="small"
              placeholder="e.g. 017"
              value={extension} onChange={(e) => setExtension(e.target.value)}
              helperText="PCO/CO number" />
          </div>
          <TextField label="New code" size="small" fullWidth
            value={newCode} disabled
            helperText="Auto-generated from modifier + extension" />
          <TextField label="Display name" size="small" fullWidth
            value={name} onChange={(e) => setName(e.target.value)} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained"
          disabled={!name || !modifier || cloneMut.isPending}
          onClick={() => cloneMut.mutate()}>
          {cloneMut.isPending ? "Cloning…" : "Clone"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────
// Apply template dialog
// ──────────────────────────────────────────────────────────────
function ApplyTemplateDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const tmplsQuery = useQuery(costCodeTemplatesQueryOptions());
  const templates = tmplsQuery.data ?? [];

  const [templateId, setTemplateId] = useState<string>("");
  const [skipExisting, setSkipExisting] = useState(true);
  const [codePrefix, setCodePrefix] = useState("");
  const [result, setResult] = useState<CostCodeTemplateApplyResult | null>(null);

  const applyMut = useMutation({
    mutationFn: () =>
      api<CostCodeTemplateApplyResult>(
        `/api/cost-code-templates/${templateId}/apply`,
        {
          method: "POST",
          body: { skip_existing: skipExisting, code_prefix: codePrefix },
        },
      ),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: costCodeKeys.all });
      qc.invalidateQueries({ queryKey: costCodeTemplateKeys.all });
      toast.success(`Applied: ${r.created} created, ${r.skipped} skipped`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Apply cost-code template</DialogTitle>
      <DialogContent dividers>
        <div className="space-y-3 pt-2">
          <TextField select size="small" fullWidth label="Template"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}>
            <MenuItem value="">— pick a template —</MenuItem>
            {templates
              .filter((t: CostCodeTemplate) => t.status === "active")
              .map((t: CostCodeTemplate) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name} v{t.version}
                  {t.trade && ` · ${t.trade}`}
                </MenuItem>
              ))}
          </TextField>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Code prefix (optional)" size="small"
              placeholder="JOB42-" value={codePrefix}
              onChange={(e) => setCodePrefix(e.target.value)}
              helperText="Prepended to every applied code" />
            <label className="flex items-center gap-2 text-xs text-slate-600 self-center dark:text-slate-300">
              <Switch size="small" checked={skipExisting}
                onChange={(_, v) => setSkipExisting(v)} />
              Skip existing codes
            </label>
          </div>
          {result && (
            <div className="border border-slate-200 rounded p-3 bg-slate-50 dark:bg-gray-950 dark:border-gray-800">
              <div className="text-sm font-medium mb-1">{result.template_name}</div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <div className="text-slate-500 dark:text-slate-400">Created</div>
                  <div className="text-emerald-700 font-mono text-lg">{result.created}</div>
                </div>
                <div>
                  <div className="text-slate-500 dark:text-slate-400">Skipped</div>
                  <div className="text-slate-600 font-mono text-lg dark:text-slate-300">{result.skipped}</div>
                </div>
                <div>
                  <div className="text-slate-500 dark:text-slate-400">Errors</div>
                  <div className="text-red-700 font-mono text-lg">{result.errors.length}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained"
          disabled={!templateId || applyMut.isPending}
          onClick={() => applyMut.mutate()}>
          {applyMut.isPending ? "Applying…" : "Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────
// Create dialog (manual new code)
// ──────────────────────────────────────────────────────────────
function CostCodeFormDialog({
  target, onClose,
}: {
  target: CostCode | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = target !== null;

  const [code, setCode] = useState(target?.code ?? "");
  const [name, setName] = useState(target?.name ?? "");
  const [division, setDivision] = useState(target?.division ?? "");
  const [uom, setUom] = useState(target?.unit_of_measure ?? "");
  const [costType, setCostType] = useState<CostTypeStr | "">(
    target?.default_cost_type ?? "",
  );
  const [sort, setSort] = useState(String(target?.sort_order ?? 0));
  const [notes, setNotes] = useState(target?.notes ?? "");

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = {
        code, name,
        division: division || null,
        unit_of_measure: uom || null,
        default_cost_type: (costType || null) as CostTypeStr | null,
        sort_order: Number(sort) || 0,
        notes: notes || null,
      };
      if (isEdit && target) {
        return api(`/api/cost-codes/${target.id}`, { method: "PATCH", body: payload });
      }
      return api("/api/cost-codes", { method: "POST", body: payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: costCodeKeys.all });
      toast.success(isEdit ? "Cost code updated" : "Cost code created");
      onClose();
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? `Edit ${target?.code}` : "New cost code"}</DialogTitle>
      <DialogContent dividers>
        <div className="grid grid-cols-2 gap-3 pt-2">
          <TextField label="Code" size="small" value={code}
            onChange={(e) => setCode(e.target.value)} inputProps={{ maxLength: 80 }} />
          <TextField label="Division" size="small" value={division}
            onChange={(e) => setDivision(e.target.value)} />
          <TextField label="Name" size="small" value={name}
            onChange={(e) => setName(e.target.value)} sx={{ gridColumn: "span 2" }} />
          <TextField select label="Default cost type" size="small"
            value={costType}
            onChange={(e) => setCostType(e.target.value as CostTypeStr | "")}>
            <MenuItem value="">— none —</MenuItem>
            {COST_TYPES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField label="UoM" size="small" value={uom}
            onChange={(e) => setUom(e.target.value)} />
          <TextField label="Sort order" type="number" size="small"
            value={sort} onChange={(e) => setSort(e.target.value)} />
          <TextField label="Notes" size="small" multiline minRows={2}
            value={notes} onChange={(e) => setNotes(e.target.value)}
            sx={{ gridColumn: "span 2" }} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained"
          disabled={!code || !name || saveMut.isPending}
          onClick={() => saveMut.mutate()}>
          {saveMut.isPending ? "Saving…" : isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// ──────────────────────────────────────────────────────────────
// CSV upload dialog (unchanged shape, kept as-is)
// ──────────────────────────────────────────────────────────────
function CostCodeUploadDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const upload = useMutation({
    mutationFn: async () => {
      const csv_text = file ? await file.text() : text;
      return api("/api/cost-codes/import-csv", {
        method: "POST", body: { csv_text },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: costCodeKeys.all });
      toast.success("Imported");
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload cost codes</DialogTitle>
      <DialogContent dividers>
        <p className="text-xs text-slate-600 mb-3 dark:text-slate-300">
          Required: <code>code, name</code>. Optional: <code>division,
          default_cost_type, unit_of_measure, sort_order, notes</code>.
        </p>
        <div
          className="border-2 border-dashed border-slate-300 rounded p-3 text-center text-sm text-slate-600 mb-3 bg-slate-50 dark:bg-gray-950 dark:border-gray-700 dark:text-slate-300"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f) setFile(f);
          }}
        >
          {file ? `📄 ${file.name}` : "Drop a .csv file or paste below"}
          <input type="file" accept=".csv" className="block mx-auto mt-2 text-xs"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </div>
        <TextField multiline minRows={6} fullWidth size="small"
          value={text} onChange={(e) => setText(e.target.value)}
          placeholder="code,name&#10;L-FRAME,Rough Framing" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained"
          disabled={(!file && !text.trim()) || upload.isPending}
          onClick={() => upload.mutate()}>
          {upload.isPending ? "Importing…" : "Import"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
